"use client";

import { format } from "date-fns";
import {
  ArrowLeft,
  Bell,
  GitBranch,
  MoreHorizontal,
  Share2,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import { useEconomicCalendar } from "@/vybe-trader-client/economic-calendar";
import { Button } from "@/shadcn-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn-components/ui/tabs";
import { cn } from "@/lib/utils";
import { EconomicSeriesDetailBody } from "../../EconomicSeriesDetailPanel";
import type { ImpactLevel } from "../../impact-styles";
import {
  impactBadgeClassName,
  impactLucideIcons,
  sentimentTextClass,
  sentimentVsForecast,
} from "../../impact-styles";
import { currencyPairLabel } from "../../currency-flag-emoji";
import { seriesKey } from "@/vybe-trader-client/economic-calendar/series-key";
import { useEconomicSeriesDetail } from "../../useEconomicSeriesDetail";

interface EventDetailsScreenProps {
  event: EconomicEvent;
  onBack: () => void;
}

export default function EventDetailsScreen({
  event,
  onBack,
}: EventDetailsScreenProps) {
  const { allEvents } = useEconomicCalendar();
  const sk = seriesKey(event);
  const { occurrences } = useEconomicSeriesDetail(sk, allEvents);
  const imp = event.impact as ImpactLevel;
  const Icon = impactLucideIcons[imp];
  const pair = currencyPairLabel(event.currency);
  const tone = sentimentVsForecast(event.actual ?? "", event.forecast ?? "");

  let whenStr = "—";
  try {
    whenStr = `${format(new Date(event.dateTimeISO), "EEE, MMM d, yyyy")}`;
  } catch {
    whenStr = event.date || "—";
  }
  const whenTime =
    event.timeLabel?.trim() ||
    (() => {
      try {
        return format(new Date(event.dateTimeISO), "HH:mm");
      } catch {
        return "—";
      }
    })();

  const [tab, setTab] = useState<"overview" | "series">("overview");

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <header className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onBack}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-center text-sm font-semibold">
          Economic event
        </h1>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              aria-label="More actions"
            >
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem disabled>
              <Bell className="mr-2 h-4 w-4" /> Set alert
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Share2 className="mr-2 h-4 w-4" /> Share
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <GitBranch className="mr-2 h-4 w-4" /> Related instruments
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="shrink-0 space-y-2 border-b border-border px-4 py-3">
        <h2 className="text-lg font-semibold leading-snug">{event.name}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex max-w-full items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-xs font-medium capitalize",
              impactBadgeClassName(imp)
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span>{imp}</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-xs tabular-nums">
            <span aria-hidden>{pair.flag}</span>
            {pair.codeUpper}
          </span>
          <span className="text-xs text-muted-foreground">
            Series · <span className="font-mono tabular-nums">{occurrences.length}</span>
            {' '}release{occurrences.length === 1 ? "" : "s"} in store
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          <span>{whenStr}</span>
          <span className="mx-1">·</span>
          <span>{whenTime}</span>
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as typeof tab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="mx-2 mt-2 h-9 shrink-0">
          <TabsTrigger value="overview" className="text-xs px-4">
            Overview
          </TabsTrigger>
          <TabsTrigger value="series" className="text-xs px-4">
            Series · charts
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="overview"
          className="flex-1 overflow-y-auto px-4 pb-6 pt-4 data-[state=inactive]:hidden"
        >
          <div className="space-y-5">
            <section>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                This release
              </h3>
              <div className="space-y-2 rounded-lg border border-border bg-muted/10 p-3 text-sm">
                <Row k="Actual" className={sentimentTextClass(tone)}>
                  {event.actual?.trim() || "—"}
                </Row>
                <Row k="Forecast">{event.forecast?.trim() || "—"}</Row>
                <Row k="Previous">{event.previous?.trim() || "—"}</Row>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                About this event
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {event.name.includes("PMI")
                  ? "The Purchasing Managers' Index is a diffusion index: readings above 50 typically signal expansion versus the prior period, depending on methodology."
                  : event.name.includes("Speaks") || event.name.includes("Speak")
                  ? "Remarks by central bankers can revise market expectations around policy guidance and liquidity conditions."
                  : "Indicators like this summarize economic activity over a prior window; interpretations depend on revisions, revisions policy, and the specific release note."}
              </p>
            </section>
          </div>
        </TabsContent>

        <TabsContent
          value="series"
          className="flex-1 overflow-y-auto px-4 pb-6 pt-4 data-[state=inactive]:hidden"
        >
          <EconomicSeriesDetailBody occurrences={occurrences} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({
  k,
  children,
  className,
}: {
  k: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className={cn("font-semibold tabular-nums text-right", className)}>
        {children}
      </span>
    </div>
  );
}
